import React, { ReactNode } from 'react';

import { IntlShape, MessageDescriptor } from 'react-intl';

import { ReactTableEnums } from '@nhl/nhl-breakaway-ui';
import { AccessorKeyColumnDef, createColumnHelper } from '@tanstack/react-table';

import { HeaderCell } from '../../../components/tableCells';
import { milestoneMessages } from '../milestoneMessages';

export const columnHelper = createColumnHelper<Record<string, any>>();

type ColumnObject = {
    size: number;
    meta: {
        style?: {
            textAlign: string;
        };
        className?: string;
        headerClassName?: string;
        pinned?: string;
    };
    accessor: any;
    header: () => ReactNode;
    minSize: number;
    id: any;
    cell: ({ row }: { row: any }) => any;
    sortingFn?: (rowA, rowB) => number;
};

const createColumn =
    (intl: IntlShape, milestoneType: string, milestoneText: MessageDescriptor) =>
    (
        columnName: string,
        header: MessageDescriptor,
        headerTooltip = header,
    ): AccessorKeyColumnDef<Record<string, any>> => {
        const columnObj: ColumnObject = {
            accessor: columnName,
            id: columnName,
            cell: ({ row }) => row.original[columnName],
            header: () => <HeaderCell tooltip={intl.formatMessage(headerTooltip)} label={intl.formatMessage(header)} />,
            meta: {
                style: {
                    textAlign: 'center',
                },
            },
            size: 50,
            minSize: 50,
        };

        switch (columnName) {
            case 'milestone':
                columnObj.minSize = 100;
                columnObj.cell = ({ row }) =>
                    `${row.original.milestoneAmount.toLocaleString()} ${intl.formatMessage(milestoneText)}`;
                columnObj.sortingFn = (rowA, rowB) => rowA.original.milestoneAmount - rowB.original.milestoneAmount;
                break;
            case 'toiMinutes':
                columnObj.cell = ({ row }) => row.original.toiMinutes.toLocaleString();
                columnObj.minSize = 55;
                break;
            case 'playerFullName':
                columnObj.minSize = 130;
                columnObj.meta = {
                    className: 'paddedColumn left-aligned',
                    headerClassName: 'paddedColumn left-aligned',
                    pinned: ReactTableEnums.Pinned.LEFT,
                };
                columnObj.cell = ({ row }) =>
                    row.original[milestoneType] === row.original.milestoneAmount ? (
                        <b>* {row.original.playerFullName}</b>
                    ) : (
                        row.original.playerFullName
                    );
                break;
            case 'teamAbbrev':
                columnObj.minSize = 60;
                break;
            default:
                break;
        }

        columnObj.size = columnObj.minSize;
        return columnHelper.accessor(columnName, columnObj);
    };

const goalieColumnDefinitions = (intl: IntlShape, milestoneType: string, milestoneText: MessageDescriptor) => {
    const columnBuilder = createColumn(intl, milestoneType, milestoneText);
    return [
        columnBuilder(
            'playerFullName',
            milestoneMessages.labels.playerFullName,
            milestoneMessages.labels.playerFullName,
        ),
        columnBuilder('teamAbbrev', milestoneMessages.labels.currentTeam),
        columnBuilder(
            'gamesPlayed',
            milestoneMessages.columnAbbrevs.games_played,
            milestoneMessages.labels.games_played,
        ),
        columnBuilder('wins', milestoneMessages.columnAbbrevs.wins, milestoneMessages.labels.wins),
        columnBuilder('so', milestoneMessages.columnAbbrevs.shutouts, milestoneMessages.labels.shutouts),
        columnBuilder('toiMinutes', milestoneMessages.columnAbbrevs.minutes, milestoneMessages.labels.minutes),
        columnBuilder('milestone', milestoneMessages.labels.milestone),
    ];
};

const skaterColumnDefinitions = (intl: IntlShape, milestoneType: string, milestoneText: MessageDescriptor) => {
    const columnBuilder = createColumn(intl, milestoneType, milestoneText);
    return [
        columnBuilder(
            'playerFullName',
            milestoneMessages.labels.playerFullName,
            milestoneMessages.labels.playerFullName,
        ),
        columnBuilder('teamAbbrev', milestoneMessages.labels.currentTeam),
        columnBuilder(
            'gamesPlayed',
            milestoneMessages.columnAbbrevs.games_played,
            milestoneMessages.labels.games_played,
        ),
        columnBuilder('goals', milestoneMessages.columnAbbrevs.goals, milestoneMessages.labels.goals),
        columnBuilder('assists', milestoneMessages.columnAbbrevs.assists, milestoneMessages.labels.assists),
        columnBuilder('points', milestoneMessages.columnAbbrevs.points, milestoneMessages.labels.points),
        columnBuilder('milestone', milestoneMessages.labels.milestone),
    ];
};

export { goalieColumnDefinitions, skaterColumnDefinitions };
